import { ref, type Ref, watch } from "vue"
import type { BaseModel } from "@/models/base.model"
import ModelManager from "@/services/model-manager"

export function useRelatedObjectsManager<T extends BaseModel, U extends BaseModel>(
  relatedObjects: Ref<U[]>,
  getModelId: (model: T) => string,
  getRelatedObjectId: (model: U) => string | undefined,
  getArray: (relatedObject: U) => T[] | undefined,
  relatedObjectRetrievalFunction?: (relatedModelId: string) => Promise<U | undefined>
) {
  const parkedModels = ref(new Map<string, BaseModel[]>())

  function pushToRelatedObjects(model: T) {
    const relatedObject = relatedObjects.value.find(
      (obj) => getModelId(model) === getRelatedObjectId(obj)
    )
    if (relatedObject) {
      // Check if the model is not already in the array.
      const array = getArray(relatedObject)
      if (array && !array.find((p) => p.id === model.id)) {
        array.push(model)
      }
    } else {
      parkModel(model) // Park the model until the related object is available
    }
  }

  function removeFromRelatedObjects(model: T) {
    const relatedObject = relatedObjects.value.find(
      (obj) => getModelId(model) === getRelatedObjectId(obj)
    )
    if (relatedObject) {
      const array = getArray(relatedObject)
      if (!array) {
        return
      }
      const index = array.findIndex((p) => p.id === model.id)
      if (index !== -1) {
        array.splice(index, 1)
      }
    }

    if (model.isManaged) {
      // If the model is managed by ModelManager, delete it.
      ModelManager.getInstance().delete(model.id!)
    }
  }

  /**
   * Park a model until the related object is available.
   * Example: Our client (A) is loading an Order (=related model). The order has a list of two Instructions.
   * While the Order is being fetched, client B creates a new Instruction for that same Order.
   * The socket will send the new Instruction to client A, but the Order is not available yet when
   * pushToRelatedObjects() is called.
   * To solve this, we park the Instruction until the Order comes available.
   * TODO: remove parked models after a certain time to conserve memory.
   * TODO: find an alternative to scanning the entire relatedObjects array on every change. Ideally we would hook...
   * TODO ...into the push function of the Order array.
   */
  function parkModel(model: T) {
    const relatedModelId = getModelId(model) // Ensure getModelId correctly returns a number
    if (!parkedModels.value.has(relatedModelId)) {
      parkedModels.value.set(relatedModelId, [])
    }
    const models = parkedModels.value.get(relatedModelId)
    if (models) {
      models.push(model)
    }
    if (relatedObjectRetrievalFunction) {
      relatedObjectRetrievalFunction(relatedModelId).then()
    }
  }

  watch(
    () => relatedObjects.value.length,
    () => {
      // If there are no parked models, there is nothing to do.
      if (parkedModels.value.size === 0) {
        return
      }

      // Loop through all relatedObjects.
      for (const relatedObject of relatedObjects.value) {
        const relatedModelId = getRelatedObjectId(relatedObject)
        if (!relatedModelId) {
          continue
        }
        const parkedModelsArray = parkedModels.value.get(relatedModelId) as T[]

        if (parkedModelsArray) {
          const array = getArray(relatedObject) // Get the array of the related object
          if (parkedModelsArray) {
            // Loop through the parked models for this related object.
            for (const parkedModel of parkedModelsArray) {
              // Check if the parked model is not deleted and not already in the array.
              if (
                !ModelManager.getInstance().isDeleted(parkedModel.id!) &&
                array &&
                !array.find((p) => p.id === parkedModel.id)
              ) {
                array.push(parkedModel)
              }
            }
          }
          parkedModels.value.delete(relatedModelId)
        }
      }
    }
  )

  return { pushToRelatedObjects, removeFromRelatedObjects }
}
